import { useRef } from 'react';
import { CaseCard } from '../components/CaseCard.jsx';
import { HeroVideo } from '../components/HeroVideo.jsx';
import { MiniNav } from '../components/MiniNav.jsx';
import { ProgressBar } from '../components/ProgressBar.jsx';
import { Sparkles } from '../components/Sparkles.jsx';
import { VideoBackdrop } from '../components/VideoBackdrop.jsx';
import { RockingPanel } from '../components/RockingPanel.jsx';
import { SiteNote } from '../components/SiteNote.jsx';
import { StatNumber } from '../components/StatNumber.jsx';
import { useRevealAll } from '../components/Reveal.jsx';
import { portfolioContent } from '../content/portfolioContent.js';

const { hero, who, work, experience, credentials, contact, footer } = portfolioContent;

export function FlatMode({ journeyAllowed, onJourney, onBreakdown, overlayOpen }) {
  const rootRef = useRef(null);
  useRevealAll(rootRef);

  return (
    <div className="content-shell" ref={rootRef}>
      <ProgressBar />
      <MiniNav hidden={overlayOpen} />

      {/* ============ HERO ============ */}
      <header className="hero" id="hero">
        <HeroVideo />
        <div className="container">
          <h1>{hero.name}</h1>
          <div className="hero-sparkle-band" aria-hidden="true">
            <div className="hero-spark-line hero-spark-line-wide" />
            <div className="hero-spark-line" />
            <Sparkles />
          </div>
          <p className="hero-line">
            {hero.lineBefore}
            <span className="accent-text">{hero.lineAccent}</span>
          </p>
          <div className="cta-row">
            {hero.actions.map((action) => (
              <a
                key={action.label}
                href={action.href}
                className={`btn${action.primary ? ' btn-primary' : ' btn-secondary'}`}
                {...(action.external ? { target: '_blank', rel: 'noopener' } : {})}
              >
                {action.label}
              </a>
            ))}
            {journeyAllowed && (
              <button type="button" className="btn btn-journey" onClick={onJourney}>
                {hero.journeyButton} ↓
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ============ WHO I AM ============ */}
      <section className="who" id="who">
        <div className="container">
          <span className="eyebrow reveal">{who.eyebrow}</span>
          <RockingPanel as="div" className="reveal">
            <div className="who-body">
              {who.paragraphs.map((para, index) => (
                <p key={index}>
                  {para.before}
                  {para.stat && <StatNumber target={para.stat.target} suffix={para.stat.suffix} className="inline-stat" />}
                  {para.after}
                </p>
              ))}
            </div>
            <div className="edge-tags">
              {who.tags.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </RockingPanel>
        </div>
      </section>

      {/* ============ CASE STUDIES ============ */}
      <section className="work" id="work">
        <VideoBackdrop src="/assets/video/work-loop.mp4" className="work-video" />
        <div className="container">
          <span className="eyebrow reveal">{work.eyebrow}</span>
          <h2 className="reveal">{work.title}</h2>
          <p className="work-intro reveal">{work.intro}</p>
          <div className="case-grid">
            {work.cases.map((caseData) => (
              <CaseCard key={caseData.id} data={caseData} onBreakdown={onBreakdown} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ EXPERIENCE ============ */}
      <section className="experience" id="experience">
        <div className="container">
          <span className="eyebrow reveal">{experience.eyebrow}</span>
          <h2 className="reveal">{experience.title}</h2>
          <RockingPanel as="div" className="role-card reveal">
            <div className="role-head">
              <div className="role-heading">
                <h3 className="role-title">{experience.roleTitle}</h3>
                <p className="role-reframe">{experience.roleReframe}</p>
              </div>
              <p className="role-meta">
                {experience.roleMeta[0]}
                <br />
                {experience.roleMeta[1]}
              </p>
            </div>
            <p className="role-lead">{experience.lead}</p>
            <div className="stat-strip">
              {experience.stats.map((stat) => (
                <div key={stat.label} className="strip-stat">
                  <StatNumber target={stat.target} suffix={stat.suffix} />
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
            <ul className="role-duties">
              {experience.duties.map((duty) => (
                <li key={duty.strong}>
                  <strong>{duty.strong}</strong>
                  {duty.text}
                </li>
              ))}
            </ul>
          </RockingPanel>
        </div>
      </section>

      {/* ============ CREDENTIALS ============ */}
      <section className="credentials" id="credentials">
        <div className="container">
          <span className="eyebrow reveal">{credentials.eyebrow}</span>
          <h2 className="reveal">{credentials.title}</h2>
          <RockingPanel as="div" className="reveal">
            <span className="cert-group-label">{credentials.certLabel}</span>
            <div className="cert-groups" style={{ marginTop: 'var(--space-sm)' }}>
              {credentials.certGroups.map((group) => (
                <div key={group.label} className="cert-group">
                  <span className="cert-group-label">{group.label}</span>
                  <ul className="cert-list">
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="skill-groups" id="skills">
              {credentials.skillGroups.map((group) => (
                <div key={group.label} className="cert-group">
                  <span className="cert-group-label">{group.label}</span>
                  <ul className="cert-list">
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="cred-secondary">
              <div className="reveal reveal-d1">
                <span className="cert-group-label">{credentials.eduLabel}</span>
                <ul className="cert-list">
                  {credentials.education.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="reveal reveal-d2">
                <span className="cert-group-label">{credentials.toolsLabel}</span>
                <div className="chip-row">
                  {credentials.tools.map((tool) => (
                    <span key={tool} className="chip-outline">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </RockingPanel>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section className="contact" id="contact">
        <div className="container">
          <RockingPanel as="div" className="reveal">
            <span className="eyebrow">{contact.eyebrow}</span>
            <h2>{contact.title}</h2>
            <p className="contact-line">{contact.line}</p>
            <div className="cta-row">
              {contact.actions.map((action) => (
                <a
                  key={action.label}
                  href={action.href}
                  className={`btn${action.primary ? ' btn-primary' : ' btn-secondary'}`}
                  {...(action.external ? { target: '_blank', rel: 'noopener' } : {})}
                >
                  {action.label}
                </a>
              ))}
              {contact.site && (
                <a className="btn btn-site" href={contact.site.href} target="_blank" rel="noopener">
                  {contact.site.label} <span aria-hidden="true">↗</span>
                </a>
              )}
            </div>
            <SiteNote note={contact.site?.note} />
          </RockingPanel>
        </div>
      </section>

      <footer className="site-footer">
        <div className="footer-inner">
          <span>{footer.left}</span>
          <span>{footer.right}</span>
        </div>
        <div className="footer-inner">
          <span className="footer-credit">{footer.credit}</span>
        </div>
      </footer>
    </div>
  );
}
